// pages/admin.tsx
import React, {
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
  useContext,
  createContext,
} from 'react';
import { Header } from 'components/Header/Header';
import { newServer } from 'constants/constants';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';

/* ------------------------- Interfaces comunes ------------------------- */
interface FormDataType {
  title: string;
  keywords: string;
  description: string;
  image: File | null;
}

interface Lawyer {
  uuid: string;
  first_name: string;
  last_name: string;
  firm_name?: string;
}

interface Submerchant {
  submerchant_id: string;
  name: string;
  info_usio: {
    legalName: string;
  };
}

interface SubmerchantUser {
  submerchant_id: string;
  name: string;
  info_usio: {
    legalName: string;
  };
}

/* ------------------------- SEO Form Genérico para Páginas/Directorios ------------------------- */
interface SeoFormProps {
  seoType: string;
  sectionTitle: string;
  description: string;
}

// Crea el contexto para la lista de abogados
const LawyersContext = createContext<Lawyer[]>([]);

// Componente proveedor que se encarga de obtener la data y proveerla a sus hijos
export const LawyersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lawyers, setLawyers] = useState<Lawyer[]>([]);

  useEffect(() => {
    const fetchLawyers = async () => {
      try {
        const response = await fetch(`${newServer}/directory/individuals`, { method: 'GET' });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setLawyers(data.users || []);
      } catch (error) {
        console.error('Error fetching lawyers:', error);
      }
    };

    fetchLawyers();
  }, []);

  return <LawyersContext.Provider value={lawyers}>{children}</LawyersContext.Provider>;
};

// Custom hook para acceder a la lista de abogados
export const useLawyers = () => {
  return useContext(LawyersContext);
};

const SeoForm: React.FC<SeoFormProps> = ({ seoType, sectionTitle, description }) => {
  const [formData, setFormData] = useState<FormDataType>({
    title: '',
    keywords: '',
    description: '',
    image: null,
  });
  const [message, setMessage] = useState<string>('');
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'image') {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        setFormData((prev) => ({ ...prev, image: file }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Actualiza la preview de la imagen cuando se selecciona un archivo
  useEffect(() => {
    if (formData.image) {
      const objectUrl = URL.createObjectURL(formData.image);
      setPreviewImageUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreviewImageUrl('');
    }
  }, [formData.image]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData();
    data.append('title', formData.title);
    data.append('keywords', formData.keywords);
    data.append('description', formData.description);
    if (formData.image) {
      data.append('image', formData.image);
    }
    try {
      const res = await fetch(`${newServer}/seo-settings/${seoType}`, {
        method: 'POST',
        body: data,
      });
      if (res.ok) {
        setMessage('Configuración actualizada correctamente.');
      } else {
        const errorData = await res.json();
        setMessage('Error: ' + errorData.error);
      }
    } catch (error) {
      setMessage('Error al conectar con el servidor.');
    }
  };

  const getSettings = async () => {
    try {
      const response = await fetch(`${newServer}/seo-settings/${seoType}`, {
        method: 'GET',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const resultData = await response.json();
      const [result] = resultData;
      if (result) {
        setFormData({
          title: result.title || '',
          keywords: result.keywords || '',
          description: result.description || '',
          image: null,
        });
      }
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
    }
  };

  useEffect(() => {
    getSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <h2 className='text-2xl font-bold mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        {sectionTitle}
      </h2>
      <p className='text-gray-600 mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        {description}
      </p>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Formulario */}
        <form onSubmit={handleSubmit} encType='multipart/form-data' className='space-y-4'>
          <div>
            <label
              htmlFor={`title-${seoType}`}
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Title (max 60 caracteres):
            </label>
            <input
              type='text'
              id={`title-${seoType}`}
              name='title'
              value={formData.title}
              maxLength={60}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese el título'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor={`keywords-${seoType}`}
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Keywords (max 160 caracteres):
            </label>
            <input
              type='text'
              id={`keywords-${seoType}`}
              name='keywords'
              value={formData.keywords}
              maxLength={160}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese las palabras clave separadas por comas'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor={`description-${seoType}`}
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Description (max 300 caracteres):
            </label>
            <textarea
              id={`description-${seoType}`}
              name='description'
              value={formData.description}
              maxLength={300}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese la descripción'
              style={{ fontFamily: 'Poppins, sans-serif', resize: 'vertical', height: '100px' }}
            />
          </div>
          <div>
            <label
              htmlFor={`image-${seoType}`}
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Image (max 72 KB):
            </label>
            <input
              type='file'
              id={`image-${seoType}`}
              name='image'
              accept='image/*'
              onChange={handleChange}
              className='mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer p-2'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <button
            type='submit'
            className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Actualizar SEO
          </button>
          {message && (
            <p
              className='mt-4 text-sm text-green-600'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

/* ------------------------- SEO para Perfiles de Abogados ------------------------- */
const SeoLawyerForm: React.FC = () => {
  const lawyers = useLawyers();
  // const [lawyers, setLawyers] = useState<Lawyer[]>([]);
  const [selectedLawyer, setSelectedLawyer] = useState<string>('');
  const [formData, setFormData] = useState<FormDataType>({
    title: '',
    keywords: '',
    description: '',
    image: null,
  });
  const [message, setMessage] = useState<string>('');
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  // Actualiza la preview de la imagen
  useEffect(() => {
    if (formData.image) {
      const objectUrl = URL.createObjectURL(formData.image);
      setPreviewImageUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreviewImageUrl('');
    }
  }, [formData.image]);

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedLawyer(e.target.value);
    loadSettings(e.target.value);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'image') {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        setFormData((prev) => ({ ...prev, image: file }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const loadSettings = async (uuid: any) => {
    if (!uuid) {
      setMessage('Por favor, seleccione un usuario.');
      return;
    }
    try {
      const response = await fetch(`${newServer}seo-settings/lawyer-profiles-users/${uuid}`, {
        method: 'GET',
      });

      if (response.status === 400) {
        setFormData({
          title: '',
          keywords: '',
          description: '',
          image: null,
        });

        return setMessage('No se encontraron configuraciones para este usuario.');
      }

      const result = await response.json();
      if (result) {
        setFormData({
          title: result.seo_super_admin.title || '',
          keywords: result.seo_super_admin.keywords || '',
          description: result.seo_super_admin.description || '',
          image: null,
        });
        setMessage('Configuración cargada correctamente.');
      } else {
        setMessage('No se encontraron configuraciones para este usuario.');
      }
    } catch (error) {
      console.error('Error fetching SEO settings for lawyer user:', error);
      setMessage('Error al cargar la configuración SEO.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedLawyer) {
      setMessage('Por favor, seleccione un abogado.');
      return;
    }

    const jsonData = {
      title: formData.title,
      keywords: formData.keywords,
      description: formData.description,
      image: formData.image,
      settingsBy: 'super_admin',
    };

    try {
      const res = await fetch(`${newServer}seo-settings/lawyer-profiles/${selectedLawyer}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData),
      });
      if (res.ok) {
        setMessage('Configuración actualizada correctamente.');
      } else {
        const errorData = await res.json();
        setMessage('Error: ' + errorData.error);
      }
    } catch (error) {
      setMessage('Error al conectar con el servidor.');
    }
  };

  return (
    <div>
      <h2 className='text-2xl font-bold mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        SEO Lawyers
      </h2>
      <p className='text-gray-600 mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        Seleccione el abogado y configure la metadata para su perfil.
      </p>
      <div className='mb-4'>
        <label
          htmlFor='lawyerSelect'
          className='block text-sm font-medium text-gray-700'
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Seleccione un Abogado:
        </label>
        <select
          id='lawyerSelect'
          value={selectedLawyer}
          onChange={handleSelectChange}
          className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          <option value=''>-- Seleccione --</option>
          {lawyers.map((lawyer) => (
            <option key={lawyer.uuid} value={lawyer.uuid}>
              {lawyer.first_name} {lawyer.last_name}{' '}
              {lawyer.firm_name ? `(${lawyer.firm_name})` : ''}
            </option>
          ))}
        </select>
      </div>
      <div className='grid grid-cols-1 gap-6'>
        {/* Formulario */}
        <form onSubmit={handleSubmit} encType='multipart/form-data' className='space-y-4'>
          <div>
            <label
              htmlFor='title-lawyer'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Title (max 60 caracteres):
            </label>
            <input
              type='text'
              id='title-lawyer'
              name='title'
              value={formData.title}
              maxLength={60}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese el título'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor='keywords-lawyer'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Keywords (max 160 caracteres):
            </label>
            <input
              type='text'
              id='keywords-lawyer'
              name='keywords'
              value={formData.keywords}
              maxLength={160}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese palabras clave separadas por comas'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor='description-lawyer'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Description (max 300 caracteres):
            </label>
            <textarea
              id='description-lawyer'
              name='description'
              value={formData.description}
              maxLength={300}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese la descripción'
              style={{ fontFamily: 'Poppins, sans-serif', resize: 'vertical', height: '100px' }}
            />
          </div>
          <div>
            <label
              htmlFor='image-lawyer'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Image (max 72 KB):
            </label>
            <input
              type='file'
              id='image-lawyer'
              name='image'
              accept='image/*'
              onChange={handleChange}
              className='mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer p-2'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <button
            type='submit'
            className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Actualizar SEO
          </button>
          {message && (
            <p
              className='mt-4 text-sm text-green-600'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

/* ------------------------- SEO para Perfiles de Abogados Users ------------------------- */
const SeoLawyerUserForm: React.FC = () => {
  const lawyers = useLawyers();
  const [selectedLawyer, setSelectedLawyer] = useState<string>('');
  const [formData, setFormData] = useState<FormDataType>({
    title: '',
    keywords: '',
    description: '',
    image: null,
  });
  const [message, setMessage] = useState<string>('');
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  // Actualiza la preview de la imagen
  useEffect(() => {
    if (formData.image) {
      const objectUrl = URL.createObjectURL(formData.image);
      setPreviewImageUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreviewImageUrl('');
    }
  }, [formData.image]);

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedLawyer(e.target.value);
    loadSettings(e.target.value);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'image') {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        setFormData((prev) => ({ ...prev, image: file }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const loadSettings = async (uuid: any) => {
    if (!uuid) {
      setMessage('Por favor, seleccione un usuario.');
      return;
    }
    try {
      const response = await fetch(`${newServer}seo-settings/lawyer-profiles-users/${uuid}`, {
        method: 'GET',
      });

      const result = await response.json();
      if (result) {
        setFormData({
          title: result.title || '',
          keywords: result.keywords || '',
          description: result.description || '',
          image: null,
        });
        setMessage('Configuración cargada correctamente.');
      } else {
        setMessage('No se encontraron configuraciones para este usuario.');
      }
    } catch (error) {
      console.error('Error fetching SEO settings for lawyer user:', error);
      setMessage('Error al cargar la configuración SEO.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedLawyer) {
      setMessage('Por favor, seleccione un abogado.');
      return;
    }

    const jsonData = {
      title: formData.title,
      keywords: formData.keywords,
      description: formData.description,
      image: formData.image,
      settingsBy: 'users',
    };

    try {
      const res = await fetch(`${newServer}seo-settings/lawyer-profiles/${selectedLawyer}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData),
      });
      if (res.ok) {
        setMessage('Configuración actualizada correctamente.');
      } else {
        const errorData = await res.json();
        setMessage('Error: ' + errorData.error);
      }
    } catch (error) {
      setMessage('Error al conectar con el servidor.');
    }
  };

  return (
    <div>
      <h2 className='text-2xl font-bold mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        SEO Lawyers Users
      </h2>
      <p className='text-gray-600 mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        Seleccione el usuario y configure la metadata para su perfil.
      </p>
      <div className='mb-4'>
        <label
          htmlFor='lawyerUserSelect'
          className='block text-sm font-medium text-gray-700'
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Seleccione un Usuario:
        </label>
        <select
          id='lawyerUserSelect'
          value={selectedLawyer}
          onChange={handleSelectChange}
          className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          <option value=''>-- Seleccione --</option>
          {lawyers.map((user) => (
            <option key={user.uuid} value={user.uuid}>
              {user.first_name} {user.last_name} {user.firm_name ? `(${user.firm_name})` : ''}
            </option>
          ))}
        </select>
      </div>
      <div className='grid grid-cols-1 gap-6'>
        {/* Formulario */}
        <form onSubmit={handleSubmit} encType='multipart/form-data' className='space-y-4'>
          <div>
            <label
              htmlFor='title-lawyer-user'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Title (max 60 caracteres):
            </label>
            <input
              type='text'
              id='title-lawyer-user'
              name='title'
              value={formData.title}
              maxLength={60}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese el título'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor='keywords-lawyer-user'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Keywords (max 160 caracteres):
            </label>
            <input
              type='text'
              id='keywords-lawyer-user'
              name='keywords'
              value={formData.keywords}
              maxLength={160}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese palabras clave separadas por comas'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor='description-lawyer-user'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Description (max 300 caracteres):
            </label>
            <textarea
              id='description-lawyer-user'
              name='description'
              value={formData.description}
              maxLength={300}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese la descripción'
              style={{ fontFamily: 'Poppins, sans-serif', resize: 'vertical', height: '100px' }}
            />
          </div>
          <div>
            <label
              htmlFor='image-lawyer-user'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Image (max 72 KB):
            </label>
            <input
              type='file'
              id='image-lawyer-user'
              name='image'
              accept='image/*'
              onChange={handleChange}
              className='mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer p-2'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <button
            type='submit'
            className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Actualizar SEO
          </button>
          {message && (
            <p
              className='mt-4 text-sm text-green-600'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

/* ------------------------- SEO para Submerchants (Bufetes de Abogados) ------------------------- */
const SeoSubmerchantForm: React.FC = () => {
  const [submerchants, setSubmerchants] = useState<Submerchant[]>([]);
  const [selectedSubmerchant, setSelectedSubmerchant] = useState<string>('');
  const [formData, setFormData] = useState<FormDataType>({
    title: '',
    keywords: '',
    description: '',
    image: null,
  });
  const [message, setMessage] = useState<string>('');
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const token = localStorage.getItem('token');
  const { user, logout } = useLogin();

  // Obtiene la lista de submerchants (bufetes)
  const fetchSubmerchants = async () => {
    try {
      const response = await fetch(`${newServer}subscriptions/submerchant-finish`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (data.status === 'JWT_EXPIRED') {
        logout();
        return;
      }
      setSubmerchants(data?.data || []);
    } catch (error) {
      console.error('Error fetching submerchant users:', error);
    }
  };

  useEffect(() => {
    fetchSubmerchants();
  }, []);

  // Actualiza la preview de la imagen
  useEffect(() => {
    if (formData.image) {
      const objectUrl = URL.createObjectURL(formData.image);
      setPreviewImageUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreviewImageUrl('');
    }
  }, [formData.image]);

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedSubmerchant(e.target.value);
    loadSettings(e.target.value);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'image') {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        setFormData((prev) => ({ ...prev, image: file }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const loadSettings = async (uuid: any) => {
    if (!uuid) {
      setMessage('Por favor, seleccione un usuario de bufete.');
      return;
    }
    try {
      const response = await fetch(`${newServer}/seo-settings/lawyer-submerchant-users/${uuid}`, {
        method: 'GET',
      });

      if (response.status === 400) {
        setFormData({
          title: '',
          keywords: '',
          description: '',
          image: null,
        });

        return setMessage('No se encontraron configuraciones para este usuario.');
      }

      const result = await response.json();

      if (result) {
        setFormData({
          title: result.seo_super_admin.title || '',
          keywords: result.seo_super_admin.keywords || '',
          description: result.seo_super_admin.description || '',
          image: null,
        });
        setMessage('Configuración cargada correctamente.');
      } else {
        setMessage('No se encontraron configuraciones para este usuario.');
      }
    } catch (error) {
      console.error('Error fetching SEO settings for submerchant user:', error);
      setMessage('Error al cargar la configuración SEO.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedSubmerchant) {
      setMessage('Por favor, seleccione un abogado.');
      return;
    }

    const jsonData = {
      title: formData.title,
      keywords: formData.keywords,
      description: formData.description,
      image: formData.image,
      settingsBy: 'super_admin',
    };

    try {
      const res = await fetch(
        `${newServer}seo-settings/lawyer-submerchant/${selectedSubmerchant}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(jsonData),
        }
      );
      if (res.ok) {
        setMessage('Configuración actualizada correctamente.');
      } else {
        const errorData = await res.json();
        setMessage('Error: ' + errorData.error);
      }
    } catch (error) {
      setMessage('Error al conectar con el servidor.');
    }
  };

  return (
    <div>
      <h2 className='text-2xl font-bold mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        SEO Submerchants
      </h2>
      <p className='text-gray-600 mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        Seleccione el bufete y configure la metadata para su sitio.
      </p>
      <div className='mb-4'>
        <label
          htmlFor='submerchantSelect'
          className='block text-sm font-medium text-gray-700'
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Seleccione un Bufete:
        </label>
        <select
          id='submerchantSelect'
          value={selectedSubmerchant}
          onChange={handleSelectChange}
          className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          <option value=''>-- Seleccione --</option>
          {submerchants.map((sub) => (
            <option key={sub.submerchant_id} value={sub.submerchant_id}>
              {sub.submerchant_id} - {sub.info_usio.legalName} - {sub.name}
            </option>
          ))}
        </select>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        <form onSubmit={handleSubmit} encType='multipart/form-data' className='space-y-4'>
          <div>
            <label
              htmlFor='title-submerchant'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Title (max 60 caracteres):
            </label>
            <input
              type='text'
              id='title-submerchant'
              name='title'
              value={formData.title}
              maxLength={60}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese el título'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor='keywords-submerchant'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Keywords (max 160 caracteres):
            </label>
            <input
              type='text'
              id='keywords-submerchant'
              name='keywords'
              value={formData.keywords}
              maxLength={160}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese palabras clave separadas por comas'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor='description-submerchant'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Description (max 300 caracteres):
            </label>
            <textarea
              id='description-submerchant'
              name='description'
              value={formData.description}
              maxLength={300}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese la descripción'
              style={{ fontFamily: 'Poppins, sans-serif', resize: 'vertical', height: '100px' }}
            />
          </div>
          <div>
            <label
              htmlFor='image-submerchant'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Image (max 72 KB):
            </label>
            <input
              type='file'
              id='image-submerchant'
              name='image'
              accept='image/*'
              onChange={handleChange}
              className='mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer p-2'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <button
            type='submit'
            className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Actualizar SEO
          </button>
          {message && (
            <p
              className='mt-4 text-sm text-green-600'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

/* ------------------------- SEO para Submerchants Users ------------------------- */
const SeoSubmerchantUserForm: React.FC = () => {
  const [submerchantUsers, setSubmerchantUsers] = useState<SubmerchantUser[]>([]);
  const [selectedSubmerchant, setSelectedSubmerchant] = useState<string>('');
  const [formData, setFormData] = useState<FormDataType>({
    title: '',
    keywords: '',
    description: '',
    image: null,
  });
  const [message, setMessage] = useState<string>('');
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const token = localStorage.getItem('token');
  const { user, logout } = useLogin();

  // Obtiene la lista de usuarios de bufetes
  const fetchSubmerchantUsers = async () => {
    try {
      const response = await fetch(`${newServer}subscriptions/submerchant-finish`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (data.status === 'JWT_EXPIRED') {
        logout();
        return;
      }
      setSubmerchantUsers(data?.data || []);
    } catch (error) {
      console.error('Error fetching submerchant users:', error);
    }
  };

  useEffect(() => {
    fetchSubmerchantUsers();
  }, []);

  useEffect(() => {
    if (formData.image) {
      const objectUrl = URL.createObjectURL(formData.image);
      setPreviewImageUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      setPreviewImageUrl('');
    }
  }, [formData.image]);

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedSubmerchant(e.target.value);
    loadSettings(e.target.value);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'image') {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        setFormData((prev) => ({ ...prev, image: file }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const loadSettings = async (uuid: any) => {
    if (!uuid) {
      setMessage('Por favor, seleccione un usuario de bufete.');
      return;
    }
    try {
      const response = await fetch(`${newServer}/seo-settings/lawyer-submerchant-users/${uuid}`, {
        method: 'GET',
      });

      if (response.status === 400) {
        setFormData({
          title: '',
          keywords: '',
          description: '',
          image: null,
        });

        return setMessage('No se encontraron configuraciones para este usuario.');
      }

      const result = await response.json();

      if (result) {
        setFormData({
          title: result.title || '',
          keywords: result.keywords || '',
          description: result.description || '',
          image: null,
        });
        setMessage('Configuración cargada correctamente.');
      } else {
        setMessage('No se encontraron configuraciones para este usuario.');
      }
    } catch (error) {
      console.error('Error fetching SEO settings for submerchant user:', error);
      setMessage('Error al cargar la configuración SEO.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedSubmerchant) {
      setMessage('Por favor, seleccione un abogado.');
      return;
    }

    const jsonData = {
      title: formData.title,
      keywords: formData.keywords,
      description: formData.description,
      image: formData.image,
      settingsBy: 'users',
    };

    try {
      const res = await fetch(
        `${newServer}seo-settings/lawyer-submerchant/${selectedSubmerchant}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(jsonData),
        }
      );
      if (res.ok) {
        setMessage('Configuración actualizada correctamente.');
      } else {
        const errorData = await res.json();
        setMessage('Error: ' + errorData.error);
      }
    } catch (error) {
      setMessage('Error al conectar con el servidor.');
    }
  };

  return (
    <div>
      <h2 className='text-2xl font-bold mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        SEO Submerchant Users
      </h2>
      <p className='text-gray-600 mb-4' style={{ fontFamily: 'Poppins, sans-serif' }}>
        Seleccione el usuario y configure la metadata para su perfil.
      </p>
      <div className='mb-4'>
        <label
          htmlFor='submerchantUserSelect'
          className='block text-sm font-medium text-gray-700'
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Seleccione un Bufete:
        </label>
        <select
          id='submerchantUserSelect'
          value={selectedSubmerchant}
          onChange={handleSelectChange}
          className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          <option value=''>-- Seleccione --</option>
          {submerchantUsers.map((user) => (
            <option key={user.submerchant_id} value={user.submerchant_id}>
              {user.submerchant_id} - {user.info_usio.legalName} - {user.name}
            </option>
          ))}
        </select>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        <form onSubmit={handleSubmit} encType='multipart/form-data' className='space-y-4'>
          <div>
            <label
              htmlFor='title-submerchant-user'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Title (max 60 caracteres):
            </label>
            <input
              type='text'
              id='title-submerchant-user'
              name='title'
              value={formData.title}
              maxLength={60}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese el título'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor='keywords-submerchant-user'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Keywords (max 160 caracteres):
            </label>
            <input
              type='text'
              id='keywords-submerchant-user'
              name='keywords'
              value={formData.keywords}
              maxLength={160}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese palabras clave separadas por comas'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <div>
            <label
              htmlFor='description-submerchant-user'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Description (max 300 caracteres):
            </label>
            <textarea
              id='description-submerchant-user'
              name='description'
              value={formData.description}
              maxLength={300}
              required
              onChange={handleChange}
              className='mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2'
              placeholder='Ingrese la descripción'
              style={{ fontFamily: 'Poppins, sans-serif', resize: 'vertical', height: '100px' }}
            />
          </div>
          <div>
            <label
              htmlFor='image-submerchant-user'
              className='block text-sm font-medium text-gray-700'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Image (max 72 KB):
            </label>
            <input
              type='file'
              id='image-submerchant-user'
              name='image'
              accept='image/*'
              onChange={handleChange}
              className='mt-1 block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer p-2'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </div>
          <button
            type='submit'
            className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Actualizar SEO
          </button>
          {message && (
            <p
              className='mt-4 text-sm text-green-600'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

/* ------------------------- Componente Principal de Configuración SEO ------------------------- */
const SeoConfig: React.FC = () => {
  return (
    <div className='min-h-screen bg-gray-50 p-6' style={{ fontFamily: 'Poppins, sans-serif' }}>
      <Header />
      <div className='max-w-7xl mx-auto mt-6 space-y-12'>
        <h1 className='text-3xl font-bold mb-6'>Configuración de SEO</h1>
        <div className='bg-white shadow rounded-lg p-6'>
          <SeoForm
            seoType='pages'
            sectionTitle='SEO para Páginas'
            description='Configura la metadata para páginas estáticas y dinámicas.'
          />
        </div>
        <LawyersProvider>
          <div
            className='container mx-auto px-4 py-8'
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            <div className='grid grid-cols-2 gap-8'>
              <div className='bg-white p-6 rounded shadow'>
                <SeoLawyerForm />
              </div>
              <div className='bg-white p-6 rounded shadow'>
                <SeoLawyerUserForm />
              </div>
            </div>
          </div>
        </LawyersProvider>
        <div className='mt-8 grid grid-cols-2 gap-8'>
          <div className='bg-white p-6 rounded shadow'>
            <SeoSubmerchantForm />
          </div>
          <div className='bg-white p-6 rounded shadow'>
            <SeoSubmerchantUserForm />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeoConfig;
